import {
	faChevronLeft,
	faEdit,
	faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useState } from "react";
import ReactPaginate from "react-paginate";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import {
	Button,
	CardLink,
	Col,
	FormGroup,
	Input,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row,
	Spinner,
} from "reactstrap";
import { Dispatch } from "redux";
import { resourceAction } from "../../actions/resourceAction";
import updownArrows from "../../assets/images/up_down_arrows.svg";
import { showAlert } from "../common/functions";
import DateFilter from "./dateFilter";

const PAGE_SIZE = 25;
const ACTIVE_COLUMN_COUNT = 9;
const HISTORY_COLUMN_COUNT = 5;

interface ResourceProps {
	dispatch: Dispatch<any>;
	resourceData: any;
}

const ResourceList: React.SFC<ResourceProps> = ({ dispatch, resourceData }) => {
	const history = useHistory();

	// Active table - order number is the only sortable column and there is no paging.
	const [activeSort, setActiveSort] = useState<any>(null);
	const [showActiveLoader, setShowActiveLoader] = useState(false);

	// History (inactive) table - search, click sorting, date range and paging.
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [searchText, setSearch] = useState<string>("");
	const [debouncedSearch, setDebouncedSearch] = useState<string>("");
	const [historySort, setHistorySort] = useState<any>(null);
	const [dateFilter, setDateFilter] = useState<any>(null);
	const [showHistoryLoader, setShowHistoryLoader] = useState(false);

	const [selectedId, setSelectedId] = useState<number>(-1);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [statusUpdateId, setStatusUpdateId] = useState<number>(-1);

	const activeResources = resourceData?.activeItems?.products ?? [];
	const historyResources = resourceData?.historyItems?.products ?? [];
	const historyCount = +(resourceData?.historyItems?.totalCount ?? 0);

	const buildActiveBody = (overrides: any = {}) => {
		const sort = "sort" in overrides ? overrides.sort : activeSort;

		return {
			sortDirection: sort?.direction ?? "",
			sortColumn: sort?.column ?? "",
			pageNumber: 1,
			pageSize: PAGE_SIZE,
			searchText: "",
			isActive: true,
			fromDate: null,
			toDate: null,
		};
	};

	const buildHistoryBody = (overrides: any = {}) => {
		const filters = {
			pageNumber: currentPage,
			searchText: debouncedSearch,
			sort: historySort,
			dateRange: dateFilter,
			...overrides,
		};

		return {
			sortDirection: filters.sort?.direction ?? "",
			sortColumn: filters.sort?.column ?? "",
			pageNumber: filters.pageNumber,
			pageSize: PAGE_SIZE,
			searchText: filters.searchText,
			isActive: false,
			fromDate: filters.dateRange?.from
				? `${filters.dateRange.from}T00:00:00`
				: null,
			toDate: filters.dateRange?.to
				? `${filters.dateRange.to}T23:59:59.999`
				: null,
		};
	};

	const loadActiveResources = (overrides: any = {}) => {
		setShowActiveLoader(true);
		dispatch(resourceAction.resourceGetActiveList(buildActiveBody(overrides)));
	};

	const loadHistoryResources = (overrides: any = {}) => {
		setShowHistoryLoader(true);
		dispatch(resourceAction.resourceGetByFilter(buildHistoryBody(overrides)));
	};

	const isSearchMounted = React.useRef(false);

	React.useEffect(() => {
		if (!isSearchMounted.current) {
			isSearchMounted.current = true;
			return;
		}
		const handler = setTimeout(() => {
			setCurrentPage(1);
			setDebouncedSearch(searchText);
		}, 400);
		return () => {
			clearTimeout(handler);
		};
	}, [searchText]);

	React.useEffect(() => {
		loadActiveResources();
	}, [activeSort]);

	React.useEffect(() => {
		loadHistoryResources({ pageNumber: currentPage });
	}, [debouncedSearch, historySort]);

	React.useEffect(() => {
		if (
			selectedId >= 0 &&
			resourceData?.isDeleteCompleted &&
			resourceData?.isSuccess &&
			!resourceData?.isError
		) {
			resourceData.isSuccess = false;
			resourceData.isDeleteCompleted = false;
			showAlert(resourceData.status, false);

			const nextPage =
				historyResources?.length === 1 && currentPage > 1
					? currentPage - 1
					: currentPage;
			setCurrentPage(nextPage);
			setSelectedId(-1);
			loadActiveResources();
			loadHistoryResources({ pageNumber: nextPage });
		}
	}, [resourceData?.isDeleteCompleted]);

	React.useEffect(() => {
		if (statusUpdateId >= 0 && resourceData?.isStatusUpdated) {
			const isFailed = resourceData.isStatusError;
			resourceData.isStatusUpdated = false;
			resourceData.isStatusError = false;
			setStatusUpdateId(-1);

			if (isFailed) {
				setShowActiveLoader(false);
				setShowHistoryLoader(false);
				if (resourceData.status === "Unauthorized") {
					history.push("/401");
				} else {
					showAlert(resourceData.status, true);
				}
			} else {
				showAlert(resourceData.status, false);
				loadActiveResources();
				loadHistoryResources({ pageNumber: currentPage });
			}
		}
	}, [resourceData?.isStatusUpdated]);

	if (
		resourceData &&
		!resourceData.isActiveLoading &&
		resourceData.isActiveListSuccess
	) {
		resourceData.isActiveListSuccess = false;
		if (showActiveLoader) {
			setShowActiveLoader(false);
		}
	}

	if (resourceData && !resourceData.isLoading && resourceData.isListSuccess) {
		resourceData.isListSuccess = false;
		if (showHistoryLoader) {
			setShowHistoryLoader(false);
		}
	}

	if (
		!resourceData.isSuccess &&
		(resourceData.isError || resourceData.isActiveError)
	) {
		if (resourceData.status === "Unauthorized") {
			history.push("/401");
		} else {
			showAlert(resourceData.status, true);
		}
		resourceData.isError = false;
		resourceData.isActiveError = false;
		setSelectedId(-1);
		if (showActiveLoader) {
			setShowActiveLoader(false);
		}
		if (showHistoryLoader) {
			setShowHistoryLoader(false);
		}
	}

	const handleSort = (sortColumn, currentSort, setSort) => {
		const direction =
			sortColumn === currentSort?.column
				? currentSort?.direction === "desc"
					? "asc"
					: "desc"
				: "desc";
		setSort({ column: sortColumn, direction: direction });
	};

	const handleGetSortColumn = (display, value, currentSort, setSort) => {
		return (
			<div
				className="column-div d-flex gap-1"
				onClick={(e) => {
					handleSort(value, currentSort, setSort);
				}}
			>
				{display}
				<img src={updownArrows} alt="updownArrows" />
			</div>
		);
	};

	const handleDateFilter = (data) => {
		setDateFilter(data);
		setCurrentPage(1);
		loadHistoryResources({ pageNumber: 1, dateRange: data });
	};

	const handleSelectForDelete = (id: any) => {
		if (activeResources.length <= 1) {
			showAlert("You need to keep at least one resource active.", true);
			return;
		}
		setSelectedId(id);
		setShowDeleteModal(true);
	};

	const handleConfirmDelete = () => {
		setShowDeleteModal(false);
		setShowActiveLoader(true);
		setShowHistoryLoader(true);
		dispatch(resourceAction.resourceDelete(selectedId));
	};

	const handleChangeStatus = (id: any, isActive: any) => {
		if (activeResources.length <= 1) {
			showAlert("You need to keep at least one resource active.", true);
			return;
		}
		setStatusUpdateId(id);
		setShowActiveLoader(true);
		setShowHistoryLoader(true);
		dispatch(resourceAction.resourceUpdateStatus(id, !isActive));
	};

	const handleCreateNew = () => {
		if (activeResources.length >= 5) {
			showAlert("Only 5 resources can be Active at a time. Please deactivate one of the existing Active resources before adding a new resource.", true);
			return;
		}
		window.location.href = "/resources?page=add";
	};

	const renderResourceRow = (data: any, isActiveTable: boolean) => (
		<tr key={data?.productId}>
			{isActiveTable && (
				<td className="icon-column">
					<a
						href={`/resources?id=${data?.productId}`}
						className="detail-link"
						data-testid="resourceEditButton"
					>
						<FontAwesomeIcon
							size="2x"
							icon={faEdit}
							title="Details"
							cursor="pointer"
							className="detailButton"
						/>
					</a>
				</td>
			)}
			<td id="resourceName">{data?.productTitle}</td>
			<td id="createdDate">
				{data?.createdAtUtc
					? new Date(data?.createdAtUtc).toLocaleDateString()
					: "-"}
			</td>
			{isActiveTable && (
				<td id="order" className="text-center">
					{data?.sortOrder ?? "-"}
				</td>
			)}
			<td id="total-clicks" className="text-center">
				{data?.totalClicks ?? 0}
			</td>
			<td id="unique-clicks" className="text-center">
				{data?.uniqueUsersClicked ?? 0}
			</td>
			<td id="guest-clicks" className="text-center">
				{data?.totalGuestUserClicks ?? 0}
			</td>
			{isActiveTable && (
				<td id="status" className="text-center">
					<FormGroup switch>
						<Input
							type="switch"
							className="active-switch"
							data-testid="resourceUpdateStatus"
							id={`resourceStatusSwitch-${data?.productId}`}
							checked={!!data?.isActive}
							onChange={() =>
								handleChangeStatus(data?.productId, data?.isActive)
							}
						/>
					</FormGroup>
				</td>
			)}
			{isActiveTable && (
				<td className="icon-column">
					<FontAwesomeIcon
						size="2x"
						icon={faTrashAlt}
						data-testid="resourceDeleteButton"
						data-bs-toggle="tooltip"
						data-bs-placement="bottom"
						cursor="pointer"
						title="Delete"
						onClick={() => handleSelectForDelete(data?.productId)}
					/>
				</td>
			)}
		</tr>
	);

	const renderTableBody = (
		resources: any[],
		isLoading: boolean,
		testId: string,
		isActiveTable: boolean
	) => {
		const columnCount = isActiveTable
			? ACTIVE_COLUMN_COUNT
			: HISTORY_COLUMN_COUNT;

		return (
			<tbody data-testid={testId}>
				{isLoading ? (
					<tr>
						<td colSpan={columnCount} className="text-center">
							<Spinner />
						</td>
					</tr>
				) : (
					<>
						{resources?.length > 0 ? (
							resources.map((data: any) => renderResourceRow(data, isActiveTable))
						) : (
							<tr>
								<td colSpan={columnCount} className="text-center h6">
									No Data found.
								</td>
							</tr>
						)}
					</>
				)}
			</tbody>
		);
	};

	return (
		<div data-testid="resourcePage" className="product-list">
			<div className="card-box">
				<div className="card-box-link-back-div">
					<FontAwesomeIcon icon={faChevronLeft} />
					<CardLink
						onClick={() => history.push("/")}
						className="card-box-link-back"
						data-testid="resource-backtolist"
					>
						Back to List
					</CardLink>
				</div>
			</div>
			<div className="card-box">
				<Row className="filter-link-container d-flex gap-1 justify-content-end">
					<Col xs="auto">
						<Button
							id="submit"
							data-testid="createNewResource"
							className="btn-submit-frm"
							onClick={handleCreateNew}
						>
							Add Resource
						</Button>
					</Col>
				</Row>
			</div>

			<h5 className="mt-4 mb-2" data-testid="activeResourcesTitle">
				Active Resources
			</h5>
			<div className="product-table">
				<table className="table table-hover" data-testid="activeResourceTable">
					<thead data-testid="activeResourceTableHead">
						<tr>
							<th scope="col" data-testid="detail-th">
								Details
							</th>
							<th scope="col" data-testid="resourcetitle-th">
								Resource Title
							</th>
							<th scope="col" data-testid="date-th">
								Added Date
							</th>
							<th scope="col" data-testid="order-th">
								{handleGetSortColumn(
									"Order Number",
									"sortorder",
									activeSort,
									setActiveSort
								)}
							</th>
							<th scope="col" data-testid="total-click-th">
								Total Clicks
							</th>
							<th scope="col" data-testid="unique-click-th">
								Unique User Clicks
							</th>
							<th scope="col" data-testid="guest-click-th">
								Guest User Clicks
							</th>
							<th scope="col" data-testid="status-th">
								Visibility Status
							</th>
							<th scope="col" data-testid="delete-th">
								Delete
							</th>
						</tr>
					</thead>
					{renderTableBody(
						activeResources,
						showActiveLoader,
						"activeResourceTableBody",
						true
					)}
				</table>
			</div>

			<h5 className="mt-4 mb-2" data-testid="historyResourcesTitle">
				History
			</h5>
			<div className="card-box">
				<Row className="filter-link-container d-flex gap-1">
					<Col xs="auto">
						<Input
							type="search"
							onChange={(event) => setSearch(event.target.value)}
							className="form-control"
							id="search-filter"
							placeholder="Search here..."
							value={searchText}
						/>
					</Col>
					<Col xs="auto">
						<DateFilter onApply={handleDateFilter} />
					</Col>
				</Row>
			</div>
			<div className="product-table">
				<table className="table table-hover" data-testid="historyResourceTable">
					<thead data-testid="historyResourceTableHead">
						<tr>
							<th scope="col" data-testid="history-resourcetitle-th">
								Resource Title
							</th>
							<th scope="col" data-testid="history-date-th">
								Added Date
							</th>
							<th scope="col" data-testid="history-total-click-th">
								{handleGetSortColumn(
									"Total Clicks",
									"totalclicks",
									historySort,
									setHistorySort
								)}
							</th>
							<th scope="col" data-testid="history-unique-click-th">
								{handleGetSortColumn(
									"Unique User Clicks",
									"uniqueuserclicks",
									historySort,
									setHistorySort
								)}
							</th>
							<th scope="col" data-testid="history-guest-click-th">
								{handleGetSortColumn(
									"Guest User Clicks",
									"guestuserclicks",
									historySort,
									setHistorySort
								)}
							</th>
						</tr>
					</thead>
					{renderTableBody(
						historyResources,
						showHistoryLoader,
						"historyResourceTableBody",
						false
					)}
				</table>
			</div>
			<div className="pagination">
				<ReactPaginate
					previousLabel={"<<"}
					nextLabel={">>"}
					breakLabel={"..."}
					pageCount={Math.ceil(historyCount / PAGE_SIZE)}
					marginPagesDisplayed={2}
					pageRangeDisplayed={3}
					onPageChange={(data) => {
						setCurrentPage(data.selected + 1);
						loadHistoryResources({ pageNumber: data.selected + 1 });
					}}
					containerClassName={"pagination"}
					pageClassName={"page-item"}
					previousClassName={"page-item"}
					breakClassName={"page-item"}
					breakLinkClassName={"page-link"}
					nextClassName={"page-item"}
					pageLinkClassName={"page-link"}
					previousLinkClassName={"page-link"}
					nextLinkClassName={"page-link"}
					activeClassName={"active"}
					forcePage={currentPage - 1}
				/>
			</div>

			<Modal
				isOpen={showDeleteModal}
				backdrop="static"
				centered
				data-testid="deleteModal"
			>
				<ModalHeader>Delete Resource</ModalHeader>
				<ModalBody className="modal-body">
					<form name="deleteResourceForm">
						<div className="col-form-label" data-testid="resourceDeleteMessage">
							Deleting this Resource will permanently remove it. To confirm,
							please click Confirm Delete.
						</div>
					</form>
				</ModalBody>
				<ModalFooter>
					<Button
						type="button"
						color="secondary"
						data-bs-dismiss="modal"
						onClick={() => {
							setShowDeleteModal(false);
							setSelectedId(-1);
						}}
						data-testid="cancelDeleteButton"
					>
						Cancel
					</Button>
					<Button
						type="button"
						color="primary"
						onClick={handleConfirmDelete}
						data-testid="confirmDeleteButton"
					>
						Confirm Delete
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
};

const mapStateToProps = (state: any) => {
	const { resourceData, languageData } = state;
	return {
		resourceData,
		languageData,
	};
};

export default connect(mapStateToProps)(ResourceList);
